import { LiveDowntimeReportEntity } from "entities/LiveDowntimeReportEntity/LiveDowntimeReportEntity";
import { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import DowntimeReport from "./DowntimeReport";
import { useStyles } from "./LiveDowntimeReport.styles";
import { IDowntimeReportData } from "./LiveDowntimeReportInterfaces";
import { ILastTwoShiftsEventsByMachineCenter } from "entities/LiveDowntimeReportEntity/LiveDowntimeReportEntityInterfaces";
import {
    DefaultTranslationFieldsInTable,
    IconsTrafficLightStatusCodes,
    MEASURE_METRICS,
    UPDATEMINUTESRATEFORHMIDATA,
} from "constants/constants";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import moment from "moment";
import MaderaLoader from "components/MaderaLoader";
import NoData from "components/NoData/NoData.view";
import { useTranslation } from "react-i18next";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";

const LiveDowntimeReport = () => {
    const classes = useStyles();
    const { millId } = useParams();
    const { t, i18n } = useTranslation();
    const { liveDowntimeReportData, loading } = LiveDowntimeReportEntity.GetMillLiveDowntimeReportData(Number(millId!));
    const [lastTwoShiftsEventsByMill, setLastTwoShiftsEventsByMill] = useState<ILastTwoShiftsEventsByMachineCenter[]>(
        [],
    );
    const { millTimezone } = useContext(LocationsContext);

    const getLastTwoShiftsMachineCentersInfoByMill = useCallback(async () => {
        try {
            const nowMillTimezone = moment().tz(millTimezone as string);

            // this condition is added because the millTimezone in the context is originally undefined until it is fed with the data from the DB
            if (nowMillTimezone) {
                const data = (await LiveDowntimeReportEntity.GetLastTwoShiftsMachineCentersInfoByMill(
                    Number(millId!),
                    nowMillTimezone,
                )) as unknown as ILastTwoShiftsEventsByMachineCenter[];

                if (data) {
                    setLastTwoShiftsEventsByMill(data);
                }
            }
        } catch {}
    }, [millId, millTimezone]);

    useEffect(() => {
        getLastTwoShiftsMachineCentersInfoByMill();

        const interval = setInterval(() => {
            getLastTwoShiftsMachineCentersInfoByMill();
        }, UPDATEMINUTESRATEFORHMIDATA);

        return () => clearInterval(interval);
    }, [getLastTwoShiftsMachineCentersInfoByMill]);

    const liveDowntimeReportDataParsed = useMemo(
        () =>
            liveDowntimeReportData
                .reduce((stack, current) => {
                    const defaultFailureTypeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        current.event_reports[0]?.failure_type,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                    );

                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        current.event_reports[0]?.failure_code,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    const existingLiveDowntimeReport = stack.findIndex(
                        (liveDowntimeReport: any) =>
                            liveDowntimeReport.id === current.production_line.production_line_id,
                    );

                    const lastTwoShiftsEvents = lastTwoShiftsEventsByMill.find(
                        (lastTwoShiftsEventsByMill) => lastTwoShiftsEventsByMill[current.machine_center_id],
                    );

                    if (existingLiveDowntimeReport !== -1) {
                        stack[existingLiveDowntimeReport] = {
                            ...stack[existingLiveDowntimeReport],
                            downtime_data: [
                                ...stack[existingLiveDowntimeReport].downtime_data,
                                {
                                    id: current.machine_center_id,
                                    name: current.name,
                                    shortName: current.short_name,
                                    machineCenterId: current.machine_center_id,
                                    machineCenterOrder: current.order,
                                    productionLine: current.production_line,
                                    isBottleneck: current.is_bottleneck,
                                    current: current.event_reports[0]?.start_date,
                                    status: current.event_reports[0]?.measure_metric_id
                                        ? current.event_reports[0]?.measure_metric_id === MEASURE_METRICS.UPTIME
                                            ? IconsTrafficLightStatusCodes.UP
                                            : IconsTrafficLightStatusCodes.DOWN
                                        : IconsTrafficLightStatusCodes.UP,
                                    failure_code: current.event_reports[0]?.failure_code
                                        ? current.event_reports[0]?.failure_code.code
                                        : null,
                                    failureCodeName: current.event_reports[0]?.failure_code
                                        ? defaultFailureCodeTranslatedName
                                        : null,
                                    failureTypeCode: current.event_reports[0]?.failure_type
                                        ? current.event_reports[0]?.failure_type.code
                                        : null,
                                    failureTypeName: current.event_reports[0]?.failure_type
                                        ? defaultFailureTypeTranslatedName
                                        : null,
                                    functionalLocationName: current.event_reports[0]?.functional_location
                                        ? handleNameTranslation(
                                              i18n.language,
                                              current.event_reports[0]?.functional_location.translation
                                                  .translation_names,
                                          )
                                        : null,
                                    areaName: current.event_reports[0]?.area
                                        ? handleNameTranslation(
                                              i18n.language,
                                              current.event_reports[0]?.area.translation.translation_names,
                                          )
                                        : null,
                                    downtime_trigger_threshold: current.downtime_trigger_threshold,
                                    millTimezone: current.production_line.mill.mill_data.timezone,
                                    millOvertime: current.production_line.mill.maximum_overtime,
                                    lastTwoShiftsEvents: lastTwoShiftsEvents,
                                },
                            ],
                        };
                    } else {
                        stack = [
                            ...stack,
                            {
                                id: current.production_line.production_line_id,
                                name: current.production_line.name,
                                downtime_data: [
                                    {
                                        id: current.machine_center_id,
                                        name: current.name,
                                        shortName: current.short_name,
                                        machineCenterId: current.machine_center_id,
                                        machineCenterOrder: current.order,
                                        productionLine: current.production_line,
                                        isBottleneck: current.is_bottleneck,
                                        current: current.event_reports[0]?.start_date,
                                        status: current.event_reports[0]?.measure_metric_id
                                            ? current.event_reports[0]?.measure_metric_id === MEASURE_METRICS.UPTIME
                                                ? IconsTrafficLightStatusCodes.UP
                                                : IconsTrafficLightStatusCodes.DOWN
                                            : IconsTrafficLightStatusCodes.UP,
                                        failure_code: current.event_reports[0]?.failure_code
                                            ? current.event_reports[0]?.failure_code.code
                                            : null,
                                        failureCodeName: current.event_reports[0]?.failure_code
                                            ? defaultFailureCodeTranslatedName
                                            : null,
                                        failureTypeCode: current.event_reports[0]?.failure_type
                                            ? current.event_reports[0]?.failure_type.code
                                            : null,
                                        failureTypeName: current.event_reports[0]?.failure_type
                                            ? defaultFailureTypeTranslatedName
                                            : null,
                                        functionalLocationName: current.event_reports[0]?.functional_location
                                            ? handleNameTranslation(
                                                  i18n.language,
                                                  current.event_reports[0]?.functional_location.translation
                                                      .translation_names,
                                              )
                                            : null,
                                        areaName: current.event_reports[0]?.area
                                            ? handleNameTranslation(
                                                  i18n.language,
                                                  current.event_reports[0]?.area.translation.translation_names,
                                              )
                                            : null,
                                        downtime_trigger_threshold: current.downtime_trigger_threshold,
                                        millTimezone: current.production_line.mill.mill_data.timezone,
                                        millOvertime: current.production_line.mill.maximum_overtime,
                                        lastTwoShiftsEvents: lastTwoShiftsEvents,
                                    },
                                ],
                            },
                        ];
                    }
                    return stack;
                }, [] as IDowntimeReportData[])
                .map((downtimeReportData) => ({
                    ...downtimeReportData,
                    downtime_data: downtimeReportData.downtime_data.find((data) => data.machineCenterOrder === null)
                        ? downtimeReportData.downtime_data
                        : [...downtimeReportData.downtime_data].sort(
                              (a, b) => Number(a.machineCenterOrder) - Number(b.machineCenterOrder),
                          ),
                })),
        [liveDowntimeReportData, i18n.language, lastTwoShiftsEventsByMill],
    );

    if ((loading && !liveDowntimeReportData) || (liveDowntimeReportDataParsed.length === 0 && loading))
        return (
            <div className={classes.liveDowntimeReportWrapperLoading}>
                <MaderaLoader />
            </div>
        );

    if (!loading && liveDowntimeReportDataParsed.length === 0)
        return (
            <div className={classes.liveDowntimeReportWrapperLoading}>
                <NoData title={t("locations.mill_dashboards.live_downtime_report.no_production_lines")} />
            </div>
        );

    return (
        <div className={classes.liveDowntimeReportWrapper}>
            {liveDowntimeReportDataParsed.map((liveDowntimeReport) => {
                return <DowntimeReport liveDowntimeReport={liveDowntimeReport} key={liveDowntimeReport.id} />;
            })}
        </div>
    );
};

export default LiveDowntimeReport;
